export default function(React, externalLibraries) {
  const { useRef, useState, useEffect } = externalLibraries;
  const { getServiceBaseUrl, serviceUrlKeys } = externalLibraries;
  const { getServiceUrlWithParams } = externalLibraries;
  const { formatCustomDateString } = externalLibraries;
  const { useText } = externalLibraries;
  const { omitBy } = externalLibraries;

  const AutosuggestEditorialSearchResult = ({ title, url, created_at, image, teaser_text }) => {
    const t = useText();

    return <li className="autosuggest-editorial__search-item">
      <a className="autosuggest__text-item text-body-medium-regular px-24 autosuggest-editorial__item-link" href={ url }>
        {
          image && image.url
            ? <img className="autosuggest-editorial__search-item-image" src={image.url} alt={image.alt} />
            : <div className="autosuggest-editorial__search-item-image media-container media-container--placeholder"></div>
        }

        <span>
          <span className="autosuggest-editorial__search-item-meta text-small-caption">
            <span className="autosuggest-editorial__search-item-type">
              { t("Artikel") }
            </span>
            <span className="autosuggest-editorial__search-item-meta-delimiter">|</span>
            <span className="autosuggest-editorial__search-item-date">{ formatCustomDateString(created_at) }</span>
          </span>

          <span className="autosuggest-editorial__search-item-title card-list-item__title text-header-h4 mb-4">
            { title }
          </span>
          <span className="autosuggest-editorial__search-item-teaser text-small-caption">
            { teaser_text }
          </span>
        </span>
      </a>
    </li>;
  }

  const editorialBundleMapping = {
    default: "Event",
    e_resource: "E-Resource"
  };

  const AutosuggestEditorialSuggestion = ({ url, title, bundle }) => {
    const t = useText();
    let mappedBundle = editorialBundleMapping[bundle] || bundle;

    return <li className="autosuggest-editorial__item">
      <a className="autosuggest__text-item text-body-medium-regular px-24 autosuggest-editorial__item-link" href={ url }>
        <span>
          { title }
          <span className="autosuggest-editorial__item-link-type">
            &nbsp;
            { mappedBundle ? "(" + t(mappedBundle) + ")" : null }
          </span>
        </span>
      </a>
    </li>;
  }

  const AutosuggestMaterial = ({ title, url, created_at, image, categories }) => {
    return (
      <li className="autosuggest-editorial__material">
        <a className="autosuggest-editorial__material-link" href={url}>
          <div className="autosuggest-editorial__material-wrapper">
            <div className="autosuggest-editorial__materia-image-wrapper">
              <img src={image.url} alt={image.alt} />
            </div>

            <div className="autosuggest-editorial__material-text">
              {categories && categories.length > 0 ? (
                <div className="autosuggest-editorial__categories">
                  {categories.map((category) => {
                    return <span key={category}>{category}</span>;
                  })}
                </div>
              ) : null}
              <h2 className="autosuggest-editorial__material-title">{title}</h2>
              <p className="autosuggest-editorial__material-date">
                {formatCustomDateString(created_at)}
              </p>
            </div>
          </div>
        </a>
      </li>
    );
  };

  // See the same option (minQueryLimit) at the `apps/search-header/search-header.tsx` file;
  const AutosuggestEditorial = ({ query, minQueryLimit = 3, materialId, filter, limit = 7, template = "suggestion", onFound }) => {
    const lastQuery = useRef("");
    const [editorialSuggestions, setEditorialSuggestions] = useState([]);

    useEffect(() => {
      if (!materialId && (!query || query.length < minQueryLimit || lastQuery.current === query))
        return;

      lastQuery.current = query || materialId;
      const serviceUrl = getServiceUrlWithParams({
        baseUrl: getServiceBaseUrl(serviceUrlKeys.dplCms),
        url: "/api/v1/editorial-search",
        params: omitBy(
          {
            page_size: limit || 7,
            q: query,
            material: materialId,
            f: filter
          },
          (value) => value == null || value === ""
        )
      });

      (async function() {
        try {
          const response = await window.fetch(serviceUrl, { mode: "cors" });
          const editorialSuggestions = await response.json();

          /* Omit response for expired query */
          if (lastQuery.current !== query && lastQuery.current !== materialId)
            return;

          if (onFound)
            onFound(editorialSuggestions);

          setEditorialSuggestions(editorialSuggestions.results);
        } catch (error) {
          console.error("Cannot fetch editorial suggestions!", error);
        }
      })();
    }, [query]);

    if (Array.isArray(editorialSuggestions) === false || editorialSuggestions.length === 0)
      return null;

    return <ul className={`autosuggest-editorial autosuggest-editorial--${ template }`}>
      {
        editorialSuggestions.map((editorialSuggestion) => {
          if (template === "search-results")
            return <AutosuggestEditorialSearchResult key={ editorialSuggestion.uuid } { ...editorialSuggestion } />;

          if (template === "material-results")
            return <AutosuggestMaterial key={ editorialSuggestion.uuid } { ...editorialSuggestion }/>;

          return <AutosuggestEditorialSuggestion key={ editorialSuggestion.uuid } { ...editorialSuggestion } />;
        })
      }
    </ul>;
  }

  return AutosuggestEditorial;
}
